import React, { useRef } from "react";
import { gsap, useGSAP, prefersReducedMotion } from "../../lib/gsap";

const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789/\\[]{}<>=+*#%";

/**
 * Readout text settles instead of fading in: each character cycles through
 * glyphs and locks left to right, the way a mechanical counter lands. Used for
 * metadata and labels — the machine half of the type system.
 *
 * The animated span is hidden from assistive tech and shadowed by a static
 * copy, so a screen reader never hears a half-scrambled string.
 */
const Decode = ({
  text,
  as: Tag = "span",
  className = "",
  duration = 0.9,
  delay = 0,
  start = "top 92%",
}) => {
  const scope = useRef(null);
  const outRef = useRef(null);

  useGSAP(
    () => {
      const el = outRef.current;
      if (!el) return undefined;

      if (prefersReducedMotion()) {
        el.textContent = text;
        return undefined;
      }

      const chars = text.split("");
      const state = { progress: 0 };

      const paint = () => {
        const locked = state.progress * chars.length;
        el.textContent = chars
          .map((char, i) => {
            if (i < Math.floor(locked)) return char;
            if (char === " ") return " ";
            return GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
          })
          .join("");
      };

      paint();

      const tween = gsap.to(state, {
        progress: 1,
        duration,
        delay,
        ease: "power2.out",
        onUpdate: paint,
        onComplete: () => {
          el.textContent = text;
        },
        scrollTrigger: { trigger: scope.current, start, once: true },
      });

      return () => {
        tween.scrollTrigger?.kill();
        tween.kill();
        el.textContent = text;
      };
    },
    { scope, dependencies: [text] }
  );

  return (
    <Tag ref={scope} className={className}>
      <span ref={outRef} aria-hidden="true">
        {text}
      </span>
      <span className="sr-only">{text}</span>
    </Tag>
  );
};

export default Decode;
